import React, { useState, useEffect, useMemo } from "react";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { Icon, IconName } from "./Icon";
import { PersonalitySelector } from "./PersonalitySelector";
import { IgnoredUsersManager } from "./IgnoredUsersManager";
import { RateLimitControl } from "./RateLimitControl";
import { ResponseModeControl } from "./ResponseModeControl";
import ConversationSummary from "./ConversationSummary";
import AnalyticsDashboard from "./AnalyticsDashboard";
import MonitoringDashboard from "./MonitoringDashboard";
import RealtimeDataWrapper from "./RealtimeDataWrapper";
import TriggerManager from "./TriggerManager";
import Account from "./Account";
import Support from "./Support";
import Admin from "./Admin";
import MemoryBank from "./MemoryBank";
import ReauthBanner from "./ReauthBanner";
import ChatAccessBanner from "./ChatAccessBanner";
import { useLoading } from "../contexts/LoadingContext";
import {
  clearSession,
  endExpiredSession,
  isSessionExpired,
} from "../auth/session";

interface BotStatus {
  channel: string;
  isActive: boolean;
  isConnected: boolean;
  personality: string;
  customContext?: string;
  createdAt: string;
  chatAccess?: "ok" | "missing" | "unknown";
}

interface DashboardProps {
  user: {
    id: string;
    username: string;
    userType: "user" | "premium" | "vip" | "admin";
    approved?: boolean;
    [key: string]: any;
  };
}

interface BotControlTabsProps {
  currentPersonality: string;
  currentCustomContext?: string;
  onUpdatePersonality: (
    personality: string,
    customContext?: string,
  ) => void | Promise<void>;
  token: string;
  userId: string;
  effectiveUserType: "user" | "premium" | "vip" | "admin";
  botUsername: string;
}

// Shared inline style so guide/section headings align their icon to the text.
const gi: React.CSSProperties = {
  verticalAlign: "-3px",
  marginRight: "8px",
};

const BotControlTabs: React.FC<BotControlTabsProps> = ({
  currentPersonality,
  currentCustomContext,
  onUpdatePersonality,
  token,
  userId,
  effectiveUserType,
  botUsername,
}) => {
  const location = useLocation();
  const currentPath = location.pathname;

  // Extract the current tab from the URL path
  const getCurrentTab = () => {
    if (currentPath.includes("/control/personality")) return "personality";
    if (currentPath.includes("/control/responses")) return "responses";
    if (currentPath.includes("/control/rate-limiting")) return "rate-limiting";
    if (currentPath.includes("/control/ignored-users")) return "ignored-users";
    return "personality"; // default
  };

  const activeTab = getCurrentTab();

  const tabs = [
    {
      id: "personality",
      name: "Personality",
      icon: "smile",
      path: "/dashboard/control/personality",
    },
    {
      id: "responses",
      name: "Responses",
      icon: "message-square",
      path: "/dashboard/control/responses",
    },
    {
      id: "rate-limiting",
      name: "Rate Limiting",
      icon: "clock",
      path: "/dashboard/control/rate-limiting",
    },
    {
      id: "ignored-users",
      name: "Ignored Users",
      icon: "ban",
      path: "/dashboard/control/ignored-users",
    },
  ];

  return (
    <div style={{ marginBottom: "20px" }}>
      {/* Tab Navigation */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #4a5568",
          marginBottom: "20px",
          overflowX: "auto",
          gap: "2px",
        }}
      >
        {tabs.map((tab) => (
          <Link
            key={tab.id}
            to={tab.path}
            style={{
              backgroundColor: activeTab === tab.id ? "#9146FF" : "transparent",
              color: activeTab === tab.id ? "white" : "#a0aec0",
              border: "none",
              padding: "12px 16px",
              borderRadius: "6px 6px 0 0",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.2s ease",
              textDecoration: "none",
            }}
          >
            <Icon
              name={tab.icon as IconName}
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />{" "}
            {tab.name}
          </Link>
        ))}
      </div>

      {/* Tab Content */}
      <Routes>
        <Route path="/" element={<Navigate to="personality" replace />} />
        <Route
          path="personality"
          element={
            <PersonalitySelector
              currentPersonality={currentPersonality}
              currentCustomContext={currentCustomContext}
              onUpdate={onUpdatePersonality}
              token={token}
              effectiveUserType={effectiveUserType}
            />
          }
        />
        <Route
          path="responses/*"
          element={
            <ResponseModeControl
              token={token}
              userType={effectiveUserType}
              botUsername={botUsername}
            />
          }
        />
        <Route
          path="rate-limiting"
          element={
            <RateLimitControl
              token={token}
              userId={userId}
              userType={effectiveUserType}
            />
          }
        />
        <Route
          path="ignored-users"
          element={<IgnoredUsersManager token={token} />}
        />
      </Routes>
    </div>
  );
};

export const Dashboard: React.FC<DashboardProps> = ({ user }) => {
  const [botStatus, setBotStatus] = useState<BotStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [isInChannel, setIsInChannel] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [currentPersonality, setCurrentPersonality] = useState("helper");
  const [botUsername, setBotUsername] = useState("hal_8580");
  const [isMobile, setIsMobile] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [tierOverride, setTierOverride] = useState<
    "user" | "premium" | "vip" | "admin" | null
  >(null);
  const location = useLocation();
  const { analyticsLoading } = useLoading();

  const token = localStorage.getItem("access_token") || "";

  // Memoize the authToken to prevent unnecessary re-renders
  const authToken = useMemo(() => token, [token]);

  // Compute effective user tier (considering admin override)
  const effectiveUserTier = tierOverride || user.userType;

  useEffect(() => {
    fetchBotStatus();

    // Refresh bot status every 10 seconds to keep UI in sync
    const interval = setInterval(() => {
      fetchBotStatus();
    }, 10000);

    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 860);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setMenuOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    setPageLoading(true);
    const timer = setTimeout(() => {
      setPageLoading(false);
    }, 50);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const fetchBotStatus = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch("/api/bot/status", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (isSessionExpired(response)) {
        endExpiredSession();
        return;
      }

      if (response.ok) {
        const data = await response.json();

        setBotStatus([data]);
        setIsInChannel(data.isActive);
        setIsConnected(data.isConnected || false);
        setCurrentPersonality(data.personality || "helper");
        setBotUsername(data.botUsername || "hal_8580");
        // Store customContext in botStatus for child components
        if (data.customContext) {
          setBotStatus([{ ...data, customContext: data.customContext }]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch bot status:", error);
    }
  };

  // Re-runs the (idempotent) join, which reconnects and retries the chat
  // subscription, e.g. after the streamer modded the bot.
  const handleCheckChatAccess = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch("/api/bot/join", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (isSessionExpired(response)) {
        endExpiredSession();
        return;
      }
    } catch (error) {
      console.error("Failed to re-check chat access:", error);
    }
    await fetchBotStatus();
  };

  const handleAddToChannel = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch("/api/bot/join", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Immediately update the UI state
        setIsInChannel(true);
        // Fetch the latest status after a short delay to allow backend to update
        setTimeout(() => {
          fetchBotStatus();
        }, 1000);
      } else {
        console.error("Failed to add bot to channel");
      }
    } catch (error) {
      console.error("Error adding bot to channel:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromChannel = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch("/api/bot/leave", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Immediately update the UI state
        setIsInChannel(false);
        setIsConnected(false);
        // Fetch the latest status after a short delay
        setTimeout(() => {
          fetchBotStatus();
        }, 1000);
      } else {
        console.error("Failed to remove bot from channel");
      }
    } catch (error) {
      console.error("Error removing bot from channel:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePersonality = async (
    personality: string,
    customContext?: string,
  ) => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch("/api/bot/personality", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ personality, customContext }),
      });

      if (response.ok) {
        setCurrentPersonality(personality);
        await fetchBotStatus();
      } else {
        throw new Error("Failed to update personality");
      }
    } catch (error) {
      console.error("Failed to update personality:", error);
      throw error;
    }
  };

  const getActiveTab = (path: string) => {
    const segments = path.split("/");
    // Check if we're in a sub-route by looking at the third segment
    if (segments[2]) {
      return segments[2]; // This will be 'analytics', 'control', 'triggers', etc.
    }
    return segments[segments.length - 1] || "control";
  };

  const activeTab = getActiveTab(location.pathname);

  const navLinkStyle = (tabName: string) => ({
    padding: "8px 16px",
    margin: "0 4px",
    textDecoration: "none",
    fontSize: "14px",
    fontWeight: "normal" as const,
    color: activeTab === tabName ? "#ffffff" : "#a0aec0",
    backgroundColor:
      activeTab === tabName ? "rgba(145, 70, 255, 0.3)" : "transparent",
    borderRadius: "6px",
    display: "inline-flex",
    alignItems: "center",
    height: "36px",
    transition: "all 0.2s",
    position: "relative" as const,
  });

  const NavLink: React.FC<{
    to: string;
    tabName: string;
    children: React.ReactNode;
    isPremium?: boolean;
    isAccessible?: boolean;
  }> = ({ to, tabName, children, isPremium = false, isAccessible = true }) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const [showTooltip, setShowTooltip] = React.useState(false);
    const isActive = activeTab === tabName;

    if (!isAccessible && isPremium) {
      // Show disabled premium feature
      return (
        <div
          style={{
            ...navLinkStyle(tabName),
            color: "#4a5568",
            cursor: "not-allowed",
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {children}
          <Icon name="lock" size={10} color="#9146FF" />
          {showTooltip && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: "50%",
                transform: "translateX(-50%)",
                marginTop: "8px",
                padding: "8px 12px",
                backgroundColor: "rgba(26, 32, 44, 0.95)",
                border: "1px solid #9146FF",
                borderRadius: "6px",
                fontSize: "12px",
                color: "#e2e8f0",
                whiteSpace: "nowrap",
                zIndex: 1000,
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
              }}
            >
              Premium feature - Upgrade to access
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        to={to}
        style={{
          ...navLinkStyle(tabName),
          backgroundColor: isActive
            ? "rgba(145, 70, 255, 0.3)"
            : isHovered
              ? "rgba(145, 70, 255, 0.15)"
              : "transparent",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {children}
      </Link>
    );
  };

  // Mobile link component
  const MobileLink: React.FC<{
    to: string;
    tabName: string;
    children: React.ReactNode;
    isPremium?: boolean;
    isAccessible?: boolean;
    onClick: () => void;
  }> = ({
    to,
    tabName,
    children,
    isPremium = false,
    isAccessible = true,
    onClick,
  }) => {
    if (!isAccessible && isPremium) {
      return (
        <div
          style={{
            ...navLinkStyle(tabName),
            display: "block",
            padding: "12px 24px",
            color: "#4a5568",
            cursor: "not-allowed",
            position: "relative",
          }}
        >
          {children}
          <span
            style={{ fontSize: "10px", color: "#9146FF", marginLeft: "8px" }}
          >
            <Icon name="lock" size={12} style={gi} />
            Premium
          </span>
        </div>
      );
    }

    return (
      <Link
        to={to}
        style={{
          ...navLinkStyle(tabName),
          display: "block",
          padding: "12px 24px",
        }}
        onClick={onClick}
      >
        {children}
      </Link>
    );
  };

  return (
    <div>
      {/* Combined Header and Navigation */}
      <div
        style={{
          position: "fixed",
          top: "0",
          left: 0,
          right: 0,
          height: "60px",
          backgroundColor: "rgba(26, 32, 44, 0.95)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 1000,
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
          padding: "0 20px",
        }}
      >
        {/* Left section - Logo */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            src="/hal-8580-logo-chip-small.png"
            alt="HAL 8580"
            onClick={() => (window.location.href = "/dashboard/control")}
            style={{
              height: "45px",
              width: "auto",
              cursor: "pointer",
              objectFit: "contain",
              marginRight: isMobile ? "0" : "40px",
              paddingTop: "5px",
            }}
          />
          {isMobile && (
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                background: "none",
                border: "none",
                color: "#e2e8f0",
                fontSize: "24px",
                cursor: "pointer",
                padding: "8px",
                marginLeft: "16px",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: "24px",
                  height: "2px",
                  backgroundColor: "#e2e8f0",
                  transition: "all 0.3s",
                }}
              ></div>
              <div
                style={{
                  width: "24px",
                  height: "2px",
                  backgroundColor: "#e2e8f0",
                  transition: "all 0.3s",
                }}
              ></div>
              <div
                style={{
                  width: "24px",
                  height: "2px",
                  backgroundColor: "#e2e8f0",
                  transition: "all 0.3s",
                }}
              ></div>
            </button>
          )}
        </div>

        {/* Center section - Navigation */}
        <div
          style={{
            display: isMobile ? "none" : "flex",
            alignItems: "center",
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <NavLink to="/dashboard/control" tabName="control">
            Bot Control
          </NavLink>
          <NavLink to="/dashboard/memorybank" tabName="memorybank">
            Memory Bank
          </NavLink>
          <NavLink to="/dashboard/triggers" tabName="triggers">
            Triggers
          </NavLink>
          <NavLink
            to="/dashboard/realtime"
            tabName="realtime"
            isPremium={true}
            isAccessible={true}
          >
            {effectiveUserTier === "premium" ||
            effectiveUserTier === "admin" ? (
              "Real-time Data"
            ) : (
              <>
                <Icon
                  name="lock"
                  size={12}
                  style={{ marginRight: "4px", verticalAlign: "-2px" }}
                />
                Real-time Data
              </>
            )}
          </NavLink>
          <NavLink
            to="/dashboard/summaries"
            tabName="summaries"
            isPremium={true}
            isAccessible={true}
          >
            {effectiveUserTier === "premium" ||
            effectiveUserTier === "admin" ? (
              "Summaries"
            ) : (
              <>
                <Icon
                  name="lock"
                  size={12}
                  style={{ marginRight: "4px", verticalAlign: "-2px" }}
                />
                Summaries
              </>
            )}
          </NavLink>
          <NavLink to="/dashboard/analytics" tabName="analytics">
            Analytics
          </NavLink>
        </div>

        {/* Right section - User profile and logout */}
        <div
          style={{
            display: isMobile ? "none" : "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          {/* Loading Spinner */}
          {(pageLoading || analyticsLoading) && (
            <div
              style={{
                width: "20px",
                height: "20px",
                border: "2px solid rgba(145, 70, 255, 0.3)",
                borderTop: "2px solid #9146FF",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
          )}
          {/* Admin Button - Only show for admin users */}
          {user.userType === "admin" && (
            <>
              <button
                onClick={() => (window.location.href = "/dashboard/admin")}
                style={{
                  backgroundColor:
                    activeTab === "admin"
                      ? "#9146FF"
                      : "rgba(145, 70, 255, 0.2)",
                  color: activeTab === "admin" ? "white" : "#9146FF",
                  border: "1px solid #9146FF",
                  padding: "6px 16px",
                  fontSize: "13px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  fontWeight: "500",
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== "admin") {
                    e.currentTarget.style.backgroundColor = "#9146FF";
                    e.currentTarget.style.color = "white";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== "admin") {
                    e.currentTarget.style.backgroundColor =
                      "rgba(145, 70, 255, 0.2)";
                    e.currentTarget.style.color = "#9146FF";
                  }
                }}
              >
                Admin
              </button>
              {/* Tier Selector - Only for admins */}
              <select
                value={tierOverride || user.userType}
                onChange={(e) =>
                  setTierOverride(
                    e.target.value === user.userType
                      ? null
                      : (e.target.value as
                          | "user"
                          | "premium"
                          | "vip"
                          | "admin"),
                  )
                }
                style={{
                  backgroundColor: "rgba(145, 70, 255, 0.2)",
                  color: "#9146FF",
                  border: "1px solid #9146FF",
                  padding: "6px 12px",
                  fontSize: "13px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  appearance: "none",
                  paddingRight: "24px",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%239146FF'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' clip-rule='evenodd' /%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 4px center",
                  backgroundSize: "16px",
                }}
                title="Test different tier views"
              >
                <option value="user">Free Tier</option>
                <option value="premium">Premium Tier</option>
                <option value="admin">Admin Tier</option>
              </select>
            </>
          )}
          <img
            src={user.profileImageUrl || (user as any).profile_image_url}
            alt={user.username}
            onClick={() => (window.location.href = "/dashboard/account")}
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              cursor: "pointer",
              objectFit: "cover",
              border:
                activeTab === "account"
                  ? "2px solid #9146FF"
                  : "2px solid transparent",
              boxShadow:
                activeTab === "account"
                  ? "0 0 0 2px rgba(145, 70, 255, 0.3)"
                  : "none",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (activeTab !== "account") {
                e.currentTarget.style.border =
                  "2px solid rgba(145, 70, 255, 0.5)";
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== "account") {
                e.currentTarget.style.border = "2px solid transparent";
              }
            }}
          />
          <button
            onClick={() => {
              clearSession();
              window.location.href = "/login";
            }}
            style={{
              backgroundColor: "rgba(145, 70, 255, 0.2)",
              color: "#9146FF",
              border: "1px solid #9146FF",
              padding: "6px 16px",
              fontSize: "13px",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "all 0.2s",
              fontWeight: "500",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#9146FF";
              e.currentTarget.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(145, 70, 255, 0.2)";
              e.currentTarget.style.color = "#9146FF";
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {isMobile && menuOpen && (
        <div
          style={{
            position: "fixed",
            top: "60px",
            left: 0,
            right: 0,
            backgroundColor: "rgba(26, 32, 44, 0.98)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
            zIndex: 998,
            padding: "10px 0",
          }}
        >
          <MobileLink
            to="/dashboard/control"
            tabName="control"
            onClick={() => setMenuOpen(false)}
          >
            Bot Control
          </MobileLink>
          <MobileLink
            to="/dashboard/memorybank"
            tabName="memorybank"
            onClick={() => setMenuOpen(false)}
          >
            Memory Bank
          </MobileLink>
          <MobileLink
            to="/dashboard/triggers"
            tabName="triggers"
            onClick={() => setMenuOpen(false)}
          >
            Triggers
          </MobileLink>
          <MobileLink
            to="/dashboard/realtime"
            tabName="realtime"
            isPremium={true}
            isAccessible={true}
            onClick={() => setMenuOpen(false)}
          >
            {effectiveUserTier === "premium" ||
            effectiveUserTier === "admin" ? (
              "Real-time Data"
            ) : (
              <>
                <Icon
                  name="lock"
                  size={12}
                  style={{ marginRight: "4px", verticalAlign: "-2px" }}
                />
                Real-time Data
              </>
            )}
          </MobileLink>
          <MobileLink
            to="/dashboard/summaries"
            tabName="summaries"
            isPremium={true}
            isAccessible={true}
            onClick={() => setMenuOpen(false)}
          >
            {effectiveUserTier === "premium" ||
            effectiveUserTier === "admin" ? (
              "Summaries"
            ) : (
              <>
                <Icon
                  name="lock"
                  size={12}
                  style={{ marginRight: "4px", verticalAlign: "-2px" }}
                />
                Summaries
              </>
            )}
          </MobileLink>
          <MobileLink
            to="/dashboard/analytics"
            tabName="analytics"
            onClick={() => setMenuOpen(false)}
          >
            Analytics
          </MobileLink>
        </div>
      )}

      {/* Tab Content - Add padding for fixed navigation */}
      <div style={{ paddingTop: "80px" }}>
        <ReauthBanner needsReauth={user.needsReauth} />
        <ChatAccessBanner
          chatAccess={isInChannel ? botStatus[0]?.chatAccess : undefined}
          botUsername={botUsername}
          onCheckAgain={handleCheckChatAccess}
        />
        <Routes>
          <Route
            path="control/*"
            element={
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  gap: "20px",
                  maxWidth: "1200px",
                  margin: "0 auto",
                  padding: "0 20px",
                }}
              >
                <div
                  style={{
                    flex: "1",
                    maxWidth: isMobile ? "100%" : "800px",
                    width: "100%",
                    order: 1,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#1a202c",
                      padding: "30px",
                      borderRadius: "12px",
                      marginBottom: "20px",
                      border: "1px solid #4a5568",
                      boxSizing: "border-box",
                      width: "100%",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {/* Background gradient effect */}
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "200px",
                        height: "200px",
                        background:
                          "radial-gradient(circle, rgba(145, 70, 255, 0.1) 0%, transparent 70%)",
                        pointerEvents: "none",
                      }}
                    />

                    <div style={{ position: "relative", zIndex: 1 }}>
                      <h2
                        style={{
                          color: "#e2e8f0",
                          marginTop: 0,
                          marginBottom: "24px",
                          fontSize: "24px",
                        }}
                      >
                        Bot Control Center
                      </h2>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "40px",
                          flexWrap: isMobile ? "wrap" : "nowrap",
                          paddingBottom: "40px",
                        }}
                      >
                        {/* HAL Image */}
                        <div
                          style={{ position: "relative", marginTop: "-5px" }}
                        >
                          <div
                            style={{
                              width: "100px",
                              height: "100px",
                              borderRadius: "50%",
                              backgroundColor: "#0a0a0a",
                              border: isConnected
                                ? "3px solid #9146FF"
                                : "3px solid #4a5568",
                              boxShadow: isConnected
                                ? "0 0 30px rgba(145, 70, 255, 0.6), inset 0 0 20px rgba(145, 70, 255, 0.3)"
                                : "0 0 10px rgba(74, 85, 104, 0.3)",
                              transition: "all 0.3s ease",
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "relative",
                            }}
                          >
                            <img
                              src="/haleye.png"
                              alt="HAL 8580"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                filter: isConnected
                                  ? "brightness(1.2) contrast(1.1)"
                                  : "brightness(0.5) grayscale(0.5)",
                                transition: "all 0.3s ease",
                              }}
                            />
                          </div>
                          {/* Status text below image */}
                          <div style={{ position: "relative" }}>
                            <div
                              style={{
                                position: "absolute",
                                bottom: "-35px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                fontSize: "11px",
                                fontWeight: "600",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                                color: isConnected ? "#ffffff" : "#718096",
                                backgroundColor: isConnected
                                  ? "#e53e3e"
                                  : "rgba(113, 128, 150, 0.2)",
                                padding: "4px 10px",
                                borderRadius: "12px",
                                border: isConnected
                                  ? "none"
                                  : "1px solid #4a5568",
                                whiteSpace: "nowrap",
                                zIndex: 2,
                              }}
                            >
                              {isConnected ? "● Online" : "● Offline"}
                            </div>
                            {/* Pulse rings */}
                            {isConnected && (
                              <>
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: "-35px",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: "80px",
                                    height: "24px",
                                    borderRadius: "12px",
                                    backgroundColor: "#e53e3e",
                                    opacity: 0,
                                    animation: "pulseRing 2s infinite",
                                    zIndex: 1,
                                  }}
                                />
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: "-35px",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: "80px",
                                    height: "24px",
                                    borderRadius: "12px",
                                    backgroundColor: "#e53e3e",
                                    opacity: 0,
                                    animation: "pulseRing 2s infinite 0.5s",
                                    zIndex: 1,
                                  }}
                                />
                              </>
                            )}
                          </div>
                        </div>

                        {/* Status and Toggle */}
                        <div style={{ flex: 1 }}>
                          <div style={{ marginBottom: "16px" }}>
                            <h3
                              style={{
                                color: "#e2e8f0",
                                margin: 0,
                                fontSize: "20px",
                                fontWeight: "600",
                              }}
                            >
                              {botUsername}
                            </h3>
                            <p
                              style={{
                                color: isInChannel ? "#48bb78" : "#e53e3e",
                                margin: "4px 0",
                                fontSize: "16px",
                                fontWeight: "500",
                              }}
                            >
                              {isInChannel
                                ? `Active in #${user.username}`
                                : "Offline"}
                            </p>
                            {isInChannel && (
                              <p
                                style={{
                                  color: "#cbd5e0",
                                  margin: "8px 0 0 0",
                                  fontSize: "14px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <span
                                  style={{
                                    display: "inline-block",
                                    padding: "4px 12px",
                                    backgroundColor: "rgba(145, 70, 255, 0.2)",
                                    borderRadius: "16px",
                                    border: "1px solid #9146FF",
                                    color: "#ddd6fe",
                                    fontWeight: "500",
                                    fontSize: "13px",
                                  }}
                                >
                                  {currentPersonality.toUpperCase()}
                                </span>
                                <span style={{ color: "#718096" }}>
                                  personality active
                                </span>
                              </p>
                            )}
                          </div>

                          {/* Big Toggle Button or Approval Warning */}
                          {user.approved !== false ? (
                            <button
                              onClick={
                                isInChannel
                                  ? handleRemoveFromChannel
                                  : handleAddToChannel
                              }
                              disabled={loading}
                              style={{
                                backgroundColor: isInChannel
                                  ? "#e53e3e"
                                  : "#48bb78",
                                color: "white",
                                border: "none",
                                padding: "14px 32px",
                                fontSize: "16px",
                                fontWeight: "600",
                                borderRadius: "8px",
                                cursor: loading ? "not-allowed" : "pointer",
                                opacity: loading ? 0.7 : 1,
                                transition: "all 0.2s",
                                boxShadow: isInChannel
                                  ? "0 4px 14px rgba(229, 62, 62, 0.3)"
                                  : "0 4px 14px rgba(72, 187, 120, 0.3)",
                                transform: "translateY(0)",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                              }}
                              onMouseEnter={(e) => {
                                if (!loading) {
                                  e.currentTarget.style.transform =
                                    "translateY(-2px)";
                                  if (isInChannel) {
                                    e.currentTarget.style.backgroundColor =
                                      "#dc2626";
                                    e.currentTarget.style.boxShadow =
                                      "0 6px 20px rgba(229, 62, 62, 0.4)";
                                  } else {
                                    e.currentTarget.style.backgroundColor =
                                      "#38a169";
                                    e.currentTarget.style.boxShadow =
                                      "0 6px 20px rgba(72, 187, 120, 0.4)";
                                  }
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform =
                                  "translateY(0)";
                                if (isInChannel) {
                                  e.currentTarget.style.backgroundColor =
                                    "#e53e3e";
                                  e.currentTarget.style.boxShadow =
                                    "0 4px 14px rgba(229, 62, 62, 0.3)";
                                } else {
                                  e.currentTarget.style.backgroundColor =
                                    "#48bb78";
                                  e.currentTarget.style.boxShadow =
                                    "0 4px 14px rgba(72, 187, 120, 0.3)";
                                }
                              }}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                {isInChannel ? (
                                  // Power off icon
                                  <path d="M18.36 6.64a9 9 0 1 1-12.73 0M12 2v10" />
                                ) : (
                                  // Power on icon
                                  <>
                                    <path d="M18.36 6.64a9 9 0 1 1-12.73 0M12 2v10" />
                                  </>
                                )}
                              </svg>
                              {loading
                                ? isInChannel
                                  ? "Deactivating..."
                                  : "Activating..."
                                : isInChannel
                                  ? "Deactivate HAL"
                                  : "Activate HAL"}
                            </button>
                          ) : (
                            <div
                              style={{
                                backgroundColor: "rgba(245, 158, 11, 0.1)",
                                border: "1px solid #f59e0b",
                                borderRadius: "8px",
                                padding: "16px",
                                marginTop: "8px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "12px",
                                }}
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#f59e0b"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  style={{ flexShrink: 0, marginTop: "2px" }}
                                >
                                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                  <line x1="12" y1="9" x2="12" y2="13" />
                                  <line x1="12" y1="17" x2="12.01" y2="17" />
                                </svg>
                                <div>
                                  <h4
                                    style={{
                                      color: "#f59e0b",
                                      margin: "0 0 8px 0",
                                      fontSize: "16px",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Account Pending Approval
                                  </h4>
                                  <p
                                    style={{
                                      color: "#cbd5e0",
                                      margin: 0,
                                      fontSize: "14px",
                                      lineHeight: "1.5",
                                    }}
                                  >
                                    Due to capacity limitations, your account
                                    needs to be approved before you can activate
                                    the bot. Please be patient - you will be
                                    notified via email when your account is
                                    approved.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Animation styles */}
                    <style>{`
                      @keyframes glow {
                        0%, 100% {
                          box-shadow: 0 0 30px rgba(145, 70, 255, 0.6), inset 0 0 20px rgba(145, 70, 255, 0.3);
                        }
                        50% {
                          box-shadow: 0 0 40px rgba(145, 70, 255, 0.8), inset 0 0 25px rgba(145, 70, 255, 0.4);
                        }
                      }

                      @keyframes pulseRing {
                        0% {
                          transform: translateX(-50%) scale(1);
                          opacity: 0.6;
                        }
                        100% {
                          transform: translateX(-50%) scale(1.8);
                          opacity: 0;
                        }
                      }

                      @keyframes spin {
                        0% {
                          transform: rotate(0deg);
                        }
                        100% {
                          transform: rotate(360deg);
                        }
                      }
                    `}</style>
                  </div>

                  {isInChannel && user.approved !== false && (
                    <BotControlTabs
                      currentPersonality={currentPersonality}
                      currentCustomContext={botStatus[0]?.customContext}
                      onUpdatePersonality={handleUpdatePersonality}
                      token={token}
                      userId={user.id}
                      effectiveUserType={effectiveUserTier}
                      botUsername={botUsername}
                    />
                  )}
                </div>

                {/* Help/Info Panel */}
                <div
                  style={{
                    width: isMobile ? "100%" : "350px",
                    maxWidth: isMobile ? "800px" : "350px",
                    backgroundColor: "rgba(45, 55, 72, 0.95)",
                    backdropFilter: "blur(5px)",
                    WebkitBackdropFilter: "blur(5px)",
                    borderRadius: "8px",
                    padding: "20px",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
                    height: "fit-content",
                    position: "relative",
                    order: isMobile ? 2 : 2,
                    marginTop: isMobile ? "0" : "0",
                    boxSizing: "border-box",
                  }}
                >
                  <h3
                    style={{
                      color: "#e2e8f0",
                      marginTop: 0,
                      marginBottom: "20px",
                      fontSize: "18px",
                    }}
                  >
                    <Icon name="info" size={18} style={gi} />
                    Getting Started
                  </h3>

                  <div style={{ marginBottom: "20px" }}>
                    <h4
                      style={{
                        color: "#9146FF",
                        marginBottom: "8px",
                        fontSize: "14px",
                      }}
                    >
                      <Icon name="bar-chart-3" size={18} style={gi} />
                      Bot Status
                    </h4>
                    <p
                      style={{
                        color: "#cbd5e0",
                        fontSize: "13px",
                        margin: 0,
                        lineHeight: "1.5",
                      }}
                    >
                      Shows whether HAL is active in your channel.
                    </p>
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <h4
                      style={{
                        color: "#9146FF",
                        marginBottom: "8px",
                        fontSize: "14px",
                      }}
                    >
                      <Icon name="gamepad-2" size={18} style={gi} />
                      Add/Remove Bot
                    </h4>
                    <p
                      style={{
                        color: "#cbd5e0",
                        fontSize: "13px",
                        margin: 0,
                        lineHeight: "1.5",
                      }}
                    >
                      Invite HAL to your stream or disconnect when not needed.
                    </p>
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <h4
                      style={{
                        color: "#9146FF",
                        marginBottom: "8px",
                        fontSize: "14px",
                      }}
                    >
                      <Icon name="smile" size={18} style={gi} />
                      Personalities
                    </h4>
                    <p
                      style={{
                        color: "#cbd5e0",
                        fontSize: "13px",
                        margin: "0 0 8px 0",
                        lineHeight: "1.5",
                      }}
                    >
                      Change how HAL interacts:
                    </p>
                    <ul
                      style={{
                        color: "#cbd5e0",
                        fontSize: "12px",
                        margin: "0 0 12px 0",
                        paddingLeft: "18px",
                      }}
                    >
                      <li>
                        <strong>HAL 8580</strong> - Classic assistant
                        personality
                      </li>
                      <li>
                        <strong>HAL DARK</strong> - Sarcastic and edgy variant
                      </li>
                    </ul>
                    <div style={{ marginLeft: "16px", marginTop: "8px" }}>
                      <p
                        style={{
                          color: "#9146FF",
                          fontSize: "13px",
                          margin: "0 0 6px 0",
                          fontWeight: "600",
                        }}
                      >
                        Premium Personalities:
                      </p>
                      <ul
                        style={{
                          color: "#a0aec0",
                          fontSize: "12px",
                          margin: 0,
                          paddingLeft: "18px",
                        }}
                      >
                        <li>
                          <strong>DJ</strong> - Music mixing and party vibes
                        </li>
                        <li>
                          <strong>Musician</strong> - Music theory and
                          composition
                        </li>
                        <li>
                          <strong>Synth Nerd</strong> - Synthesizer enthusiast
                        </li>
                        <li>
                          <strong>Gamer</strong> - Gaming culture expert
                        </li>
                        <li>
                          <strong>Chef</strong> - Culinary wisdom and recipes
                        </li>
                        <li>
                          <strong>Fitness Coach</strong> - Health and workout
                          tips
                        </li>
                        <li>
                          <strong>Custom Context</strong> - Create your own
                          unique persona
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <h4
                      style={{
                        color: "#9146FF",
                        marginBottom: "8px",
                        fontSize: "14px",
                      }}
                    >
                      <Icon name="zap" size={18} style={gi} />
                      Rate Limiting
                    </h4>
                    <p
                      style={{
                        color: "#cbd5e0",
                        fontSize: "13px",
                        margin: "0 0 8px 0",
                        lineHeight: "1.5",
                      }}
                    >
                      Prevent HAL from dominating conversations:
                    </p>
                    <ul
                      style={{
                        color: "#cbd5e0",
                        fontSize: "12px",
                        margin: "0 0 12px 0",
                        paddingLeft: "18px",
                      }}
                    >
                      <li>Set message limits per time window</li>
                      <li>Automatic cooldown periods when limit reached</li>
                      <li>Buffer messages during rate limits</li>
                    </ul>
                    <div style={{ marginLeft: "16px", marginTop: "8px" }}>
                      <p
                        style={{
                          color: "#9146FF",
                          fontSize: "13px",
                          margin: "0 0 6px 0",
                          fontWeight: "600",
                        }}
                      >
                        Premium Rate Limiting Features:
                      </p>
                      <ul
                        style={{
                          color: "#a0aec0",
                          fontSize: "12px",
                          margin: 0,
                          paddingLeft: "18px",
                        }}
                      >
                        <li>Intelligent message summarization</li>
                      </ul>
                    </div>
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <h4
                      style={{
                        color: "#9146FF",
                        marginBottom: "8px",
                        fontSize: "14px",
                      }}
                    >
                      <Icon name="target" size={18} style={gi} />
                      Response Triggers
                    </h4>
                    <p
                      style={{
                        color: "#cbd5e0",
                        fontSize: "13px",
                        margin: "0 0 8px 0",
                        lineHeight: "1.5",
                      }}
                    >
                      Control when HAL responds to messages:
                    </p>
                    <ul
                      style={{
                        color: "#cbd5e0",
                        fontSize: "12px",
                        margin: "0 0 12px 0",
                        paddingLeft: "18px",
                      }}
                    >
                      <li>
                        <strong>Keyword Mode</strong> - Responds when specific
                        keywords are detected
                      </li>
                      <li>
                        <strong>Intelligent Mode</strong> - Uses AI to determine
                        if messages are for the bot
                      </li>
                    </ul>
                    <div style={{ marginLeft: "16px", marginTop: "8px" }}>
                      <p
                        style={{
                          color: "#9146FF",
                          fontSize: "13px",
                          margin: "0 0 6px 0",
                          fontWeight: "600",
                        }}
                      >
                        Premium Feature:
                      </p>
                      <ul
                        style={{
                          color: "#a0aec0",
                          fontSize: "12px",
                          margin: 0,
                          paddingLeft: "18px",
                        }}
                      >
                        <li>
                          <strong>Intelligent Mode</strong> - AI analyzes
                          context and confidence
                        </li>
                        <li>Adjustable confidence threshold (0-100%)</li>
                        <li>
                          Reduces unwanted responses while catching intended
                          ones
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <h4
                      style={{
                        color: "#9146FF",
                        marginBottom: "8px",
                        fontSize: "14px",
                      }}
                    >
                      <Icon name="ban" size={18} style={gi} />
                      Ignored Users
                    </h4>
                    <p
                      style={{
                        color: "#cbd5e0",
                        fontSize: "13px",
                        margin: "0 0 8px 0",
                        lineHeight: "1.5",
                      }}
                    >
                      Manage users that HAL should completely ignore:
                    </p>
                    <ul
                      style={{
                        color: "#cbd5e0",
                        fontSize: "12px",
                        margin: 0,
                        paddingLeft: "18px",
                      }}
                    >
                      <li>
                        Messages from ignored users won&apos;t be read or stored
                      </li>
                      <li>
                        They won&apos;t trigger any bot responses or reactions
                      </li>
                      <li>Useful for ignoring other bots or spam accounts</li>
                      <li>No embeddings or memories will be created</li>
                    </ul>
                  </div>

                  <div
                    style={{
                      backgroundColor: "#1a202c",
                      padding: "12px",
                      borderRadius: "6px",
                      marginTop: "15px",
                      border: "1px solid #4a5568",
                    }}
                  >
                    <p
                      style={{
                        color: "#9146FF",
                        fontSize: "12px",
                        margin: "0 0 6px 0",
                        fontWeight: "bold",
                      }}
                    >
                      <Icon name="lightbulb" size={16} style={gi} />
                      Tips:
                    </p>
                    <ul
                      style={{
                        color: "#a0aec0",
                        fontSize: "11px",
                        margin: 0,
                        paddingLeft: "16px",
                      }}
                    >
                      <li>HAL responds to @mentions & replies</li>
                      <li>Set up keyword triggers</li>
                      <li>Check analytics for insights</li>
                      <li>
                        <strong>
                          If HAL isn&apos;t responding, make sure to mod him in
                          your channel!
                        </strong>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            }
          />

          <Route
            path="summaries"
            element={
              effectiveUserTier === "premium" ||
              effectiveUserTier === "admin" ? (
                <ConversationSummary
                  authToken={authToken}
                  userType={effectiveUserTier}
                />
              ) : (
                <div
                  style={{
                    maxWidth: "600px",
                    margin: "40px auto",
                    textAlign: "center",
                    padding: "40px",
                    backgroundColor: "#2d3748",
                    borderRadius: "12px",
                    border: "2px solid #4a5568",
                  }}
                >
                  <div style={{ fontSize: "48px", marginBottom: "20px" }}>
                    <Icon name="bar-chart-3" size={48} color="#9146FF" />
                  </div>
                  <h3
                    style={{
                      fontSize: "24px",
                      color: "#e2e8f0",
                      marginBottom: "16px",
                    }}
                  >
                    Conversation Summaries is a Premium Feature
                  </h3>
                  <p
                    style={{
                      fontSize: "16px",
                      color: "#cbd5e0",
                      marginBottom: "24px",
                      lineHeight: "1.6",
                    }}
                  >
                    Get AI-generated summaries of your daily stream
                    conversations. HAL analyzes chat interactions, key topics,
                    and memorable moments to create comprehensive daily
                    summaries that help track your community&apos;s evolution.
                  </p>
                  <button
                    onClick={() =>
                      (window.location.href = "/dashboard/account")
                    }
                    style={{
                      padding: "12px 24px",
                      backgroundColor: "#9146FF",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "16px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#7c3aed";
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(145, 70, 255, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#9146FF";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <Icon name="sparkles" size={16} style={gi} />
                    Upgrade to Premium
                  </button>
                </div>
              )
            }
          />

          <Route
            path="memorybank/*"
            element={
              <MemoryBank
                authToken={authToken}
                botStatus={botStatus}
                userType={effectiveUserTier}
              />
            }
          />

          <Route
            path="analytics/*"
            element={
              <AnalyticsDashboard
                authToken={authToken}
                userType={effectiveUserTier}
              />
            }
          />
          {effectiveUserTier === "admin" && (
            <Route
              path="monitoring/*"
              element={<MonitoringDashboard authToken={authToken} />}
            />
          )}
          <Route
            path="realtime/*"
            element={
              effectiveUserTier === "premium" ||
              effectiveUserTier === "admin" ? (
                <RealtimeDataWrapper authToken={authToken} />
              ) : (
                <div
                  style={{
                    maxWidth: "600px",
                    margin: "40px auto",
                    textAlign: "center",
                    padding: "40px",
                    backgroundColor: "#2d3748",
                    borderRadius: "12px",
                    border: "2px solid #4a5568",
                  }}
                >
                  <div style={{ fontSize: "48px", marginBottom: "20px" }}>
                    <Icon name="radio" size={48} color="#9146FF" />
                  </div>
                  <h3
                    style={{
                      fontSize: "24px",
                      color: "#e2e8f0",
                      marginBottom: "16px",
                    }}
                  >
                    Real-time Data is a Premium Feature
                  </h3>
                  <p
                    style={{
                      fontSize: "16px",
                      color: "#cbd5e0",
                      marginBottom: "24px",
                      lineHeight: "1.6",
                    }}
                  >
                    Provide HAL with dynamic context that updates in real-time.
                    Perfect for sub goals, current games, or any data that
                    changes during your stream. You can send the current BPM of
                    the music you play, song title, your heart rate, weather,
                    anything you can think of, and HAL will incorporate this
                    into discussions. Includes full API support for
                    integrations.
                  </p>
                  <button
                    onClick={() =>
                      (window.location.href = "/dashboard/account")
                    }
                    style={{
                      padding: "12px 24px",
                      backgroundColor: "#9146FF",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "16px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#7c3aed";
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(145, 70, 255, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#9146FF";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <Icon name="sparkles" size={16} style={gi} />
                    Upgrade to Premium
                  </button>
                </div>
              )
            }
          />
          <Route
            path="triggers/*"
            element={
              <TriggerManager
                authToken={authToken}
                userType={effectiveUserTier}
              />
            }
          />
          <Route
            path="account/*"
            element={
              <Account
                user={{ ...user, userType: effectiveUserTier }}
                authToken={authToken}
              />
            }
          />
          <Route
            path="support"
            element={<Support user={user} authToken={authToken} />}
          />
          {effectiveUserTier === "admin" ? (
            <Route path="admin/*" element={<Admin authToken={authToken} />} />
          ) : (
            <Route
              path="admin/*"
              element={<Navigate to="/dashboard" replace />}
            />
          )}
          <Route path="/" element={<Navigate to="control" replace />} />
        </Routes>
      </div>
    </div>
  );
};
